import { NgTemplateOutlet, NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FavoritesContentService } from '@modules/favorites/services/favorites-content.service';
import { FavoritesService } from '@modules/favorites/services/favorites.service';
import { ActionResourceInterface } from '@modules/shared/action-button-group/action.types';
import { DownloadService } from '@modules/shared/services/download.service';
import { ShareModalService } from '@modules/shared/services/share.service';
import { TrackingService, TRACKING_TYPES } from '@modules/shared/services/tracking.service';

export enum ActionIcons {
  SHARE = 'sharing',
  DOWNLOAD = 'download',
  BOOKMARK = 'bookmark',
  DELETE = 'delete',
}

export interface ButtonItem {
  icon: ActionIcons;
  class?: string;
  label?: string;
  labelClass?: string;
  callback?: (() => void) | ((mediaId: string) => void);
}

@Component({
  selector: 'mb-action-container',
  templateUrl: './action-container.component.html',
  styleUrls: ['./action-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgTemplateOutlet, NgClass, TranslatePipe],
})
export class ActionContainerComponent implements OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private favoritesContentService = inject(FavoritesContentService);
  private favoriteService = inject(FavoritesService);
  private shareService = inject(ShareModalService);
  private downloadService = inject(DownloadService);
  private trackingService = inject(TrackingService);
  elRef = inject(ElementRef);

  private destroy$ = new Subject<boolean>();
  public actions = ActionIcons;
  @Output()
  actionClick = new EventEmitter<ActionIcons>();

  @Input()
  buttonItems: ButtonItem[] = [];

  @Input() resource?: ActionResourceInterface;
  @Input() preMediaId?: string;

  constructor() {
    // FIXME: code smell
    this.favoritesContentService.favorites$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.resource) {
        this.cdr.detectChanges();
      }
    });
  }

  onActionClick(event: MouseEvent, actionItem: ButtonItem) {
    if (this.resource) {
      if (actionItem.icon === ActionIcons.BOOKMARK || actionItem.icon === ActionIcons.DELETE) {
        if (this.resource.favoriteData) {
          if (!this.favoriteService.isFavorite(this.resource.favoriteData.id)) {
            this.trackingService.trackEvent(this.resource, TRACKING_TYPES.BOOKMARK);
          }
          this.favoriteService.toggle(this.resource);
        }
      } else if (actionItem.icon === ActionIcons.SHARE) {
        if (this.resource.shareData) {
          this.shareService.openModal(this.resource);
          this.trackingService.trackEvent(this.resource, TRACKING_TYPES.SHARE);
        }
      } else if (actionItem.icon === ActionIcons.DOWNLOAD) {
        if (this.resource) {
          const downloadCallback = () => {
            // track download event after the user actually clicked download
            this.trackingService.trackEvent(this.resource, TRACKING_TYPES.DOWNLOAD);
            if (this.resource?.downloadData?.id) {
              actionItem.callback?.(this.resource.downloadData.id);
            }
          };

          let resourceData = this.resource;

          if (this.preMediaId) {
            if (Array.isArray(this.resource)) {
              resourceData = this.resource.map((item) => {
                if (item.downloadData) {
                  return {
                    ...item,
                    downloadData: {
                      ...item.downloadData,
                      premediaId: this.preMediaId,
                    },
                  };
                }
                return item;
              });
            } else if (this.resource.downloadData) {
              resourceData = {
                ...this.resource,
                downloadData: {
                  ...this.resource.downloadData,
                  premediaId: this.preMediaId,
                },
              };
            }
          }

          this.downloadService.download({
            data: resourceData,
            isShareDownload: false,
            pushToDataLayer: true,
            downloadCb: downloadCallback,
          });
        }
      }
    }
    this.actionClick.emit(actionItem.icon);
    event.stopPropagation();
    return false;
  }

  get isBookmarked() {
    if (!this.resource?.favoriteData) {
      return false;
    }
    return this.favoriteService.isFavorite(this.resource.favoriteData.id);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
